/**
 * draftStorage — localStorage persistence for unpublished bot drafts.
 *
 * There is no server-side draft tier: an unpublished bot setting or
 * conversation-graph edit lives in the operator's browser until "Publish".
 * Before this module a reload discarded it silently. Now:
 *
 *  - The config draft is persisted as a PATCH against the last fetched
 *    snapshot (`set` = changed keys, `unset` = keys the draft dropped), not the
 *    whole config. On reload the fresh server config is fetched first and the
 *    patch is overlaid, so an edit made by another tab in the meantime is kept
 *    and only the operator's own unpublished changes are re-applied.
 *  - Graph drafts are persisted per workflow together with the baseline they
 *    were measured against, so `GraphDraftStore.setBaseline` treats the
 *    restored entry exactly like a live one (dirty → keep, rebase only).
 *  - Publish clears the bot's keys and writes a "published" signal key. Other
 *    tabs holding that bot's config react to the `storage` event by reloading,
 *    because their in-memory snapshot is now stale.
 *
 * Every access is wrapped: localStorage can be missing (SSR, tests without
 * jsdom) or throw (private mode, quota, blocked site data). A failure here
 * degrades to the pre-persistence behaviour, never to a broken page.
 */
import { useSyncExternalStore } from 'react';
import type { BotConfig } from '../../config/types';

const PREFIX = 'voxcore:draft:';

/** Fields never written to localStorage even while unpublished. */
const EXCLUDED_FIELDS: ReadonlySet<string> = new Set(['passive_webhook_secret']);

export interface ConfigDraftPatch {
  /** Keys whose draft value differs from the saved snapshot. */
  set: Partial<BotConfig>;
  /** Keys present in the saved snapshot but removed from the draft. */
  unset: string[];
}

export interface StoredGraphDraft {
  graph: unknown;
  /** `JSON.stringify` of the server graph the draft was measured against. */
  baseline: string;
}

export function configDraftKey(botId: string): string {
  return `${PREFIX}${botId}:config`;
}

export function graphDraftKey(botId: string): string {
  return `${PREFIX}${botId}:graphs`;
}

export function publishedSignalKey(botId: string): string {
  return `${PREFIX}${botId}:published`;
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readJSON<T>(key: string): T | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Same-tab change notification. `storage` events only reach OTHER tabs, so the
 * sidebar's "unpublished changes" dot also listens for this one, fired after
 * every write that actually changed a draft key in this tab.
 */
const DRAFTS_CHANGED_EVENT = 'voxcore:drafts-changed';

function notifyDraftsChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(DRAFTS_CHANGED_EVENT));
  } catch {
    // Event construction unsupported — the dot catches up on the next change.
  }
}

function writeJSON(key: string, value: unknown | null): void {
  const s = storage();
  if (!s) return;
  try {
    const prior = s.getItem(key);
    const next = value === null ? null : JSON.stringify(value);
    if (prior === next) return;
    if (next === null) s.removeItem(key);
    else s.setItem(key, next);
  } catch {
    // Quota / blocked storage: the draft simply isn't persisted this time.
    return;
  }
  notifyDraftsChanged();
}

/**
 * Compute the unpublished changes as a patch against the saved snapshot.
 * Returns null when the draft equals the snapshot (nothing to persist).
 */
export function diffConfigDraft(
  config: Partial<BotConfig>,
  saved: Partial<BotConfig>,
): ConfigDraftPatch | null {
  const set: Record<string, unknown> = {};
  const unset: string[] = [];
  const cfg = config as Record<string, unknown>;
  const base = saved as Record<string, unknown>;
  // A key holding `undefined` counts as absent: the Sidekick clears a field
  // with `update(key, undefined)`, and JSON (which `dirty` compares) drops it.
  const has = (o: Record<string, unknown>, k: string) => k in o && o[k] !== undefined;
  for (const key of Object.keys(cfg)) {
    if (EXCLUDED_FIELDS.has(key) || !has(cfg, key)) continue;
    if (!has(base, key) || JSON.stringify(cfg[key]) !== JSON.stringify(base[key])) {
      set[key] = cfg[key];
    }
  }
  for (const key of Object.keys(base)) {
    if (EXCLUDED_FIELDS.has(key) || !has(base, key)) continue;
    if (!has(cfg, key)) unset.push(key);
  }
  if (Object.keys(set).length === 0 && unset.length === 0) return null;
  return { set: set as Partial<BotConfig>, unset };
}

/** Overlay a persisted patch on a freshly fetched config. */
export function applyConfigDraftPatch(
  base: Partial<BotConfig>,
  patch: ConfigDraftPatch,
): Partial<BotConfig> {
  const next: Record<string, unknown> = { ...(base as Record<string, unknown>), ...(patch.set as Record<string, unknown>) };
  for (const key of patch.unset) delete next[key];
  return next as Partial<BotConfig>;
}

export function readConfigDraft(botId: string): ConfigDraftPatch | null {
  const p = readJSON<ConfigDraftPatch>(configDraftKey(botId));
  if (!p || typeof p !== 'object' || !p.set || typeof p.set !== 'object' || !Array.isArray(p.unset)) return null;
  return p;
}

export function writeConfigDraft(botId: string, patch: ConfigDraftPatch | null): void {
  writeJSON(configDraftKey(botId), patch);
}

export function readGraphDrafts(botId: string): Record<string, StoredGraphDraft> {
  const v = readJSON<Record<string, StoredGraphDraft>>(graphDraftKey(botId));
  if (!v || typeof v !== 'object') return {};
  const out: Record<string, StoredGraphDraft> = {};
  for (const [id, entry] of Object.entries(v)) {
    if (entry && typeof entry === 'object' && entry.graph && typeof entry.baseline === 'string') out[id] = entry;
  }
  return out;
}

export function writeGraphDrafts(botId: string, entries: Record<string, StoredGraphDraft> | null): void {
  writeJSON(graphDraftKey(botId), entries && Object.keys(entries).length ? entries : null);
}

/** Drop every persisted draft for a bot (both config and graphs). */
export function clearBotDrafts(botId: string): void {
  writeJSON(configDraftKey(botId), null);
  writeJSON(graphDraftKey(botId), null);
}

/**
 * Tell other tabs this bot was just published. `storage` events fire only in
 * OTHER same-origin tabs, which is exactly the audience: the publishing tab has
 * already refetched. The value must change each time or no event fires.
 */
export function announceBotPublished(botId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(publishedSignalKey(botId), `${Date.now()}:${Math.random().toString(36).slice(2)}`);
  } catch {
    // Without storage there are no other tabs to notify through it.
  }
}

/** Subscribe to another tab's publish of `botId`. Returns the unsubscribe. */
export function subscribeBotPublished(botId: string, cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const key = publishedSignalKey(botId);
  const onStorage = (e: StorageEvent) => {
    if (e.key === key && e.newValue) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Reload this tab. Separate so tests can stub it (jsdom can't navigate). */
export function reloadPage(): void {
  if (typeof window === 'undefined') return;
  window.location.reload();
}

/** What a bot has persisted but not yet published. */
export interface BotDraftState {
  /** The config draft differs from the last fetched config. */
  config: boolean;
  /** Workflow ids with an unpublished graph edit. */
  workflowIDs: string[];
  /** Either of the above. */
  any: boolean;
}

const EMPTY_DRAFT_STATE: BotDraftState = { config: false, workflowIDs: [], any: false };

/**
 * Read a bot's persisted draft state. The keys exist iff the draft is dirty
 * (both writers remove them once clean), so presence is the whole signal and
 * this works for bots this tab has never loaded.
 */
export function readBotDraftState(botId: string): BotDraftState {
  const s = storage();
  if (!s || !botId) return EMPTY_DRAFT_STATE;
  let config = false;
  try {
    config = s.getItem(configDraftKey(botId)) !== null;
  } catch {
    return EMPTY_DRAFT_STATE;
  }
  const workflowIDs = Object.keys(readGraphDrafts(botId)).sort();
  if (!config && workflowIDs.length === 0) return EMPTY_DRAFT_STATE;
  return { config, workflowIDs, any: true };
}

function subscribeDrafts(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(PREFIX)) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(DRAFTS_CHANGED_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(DRAFTS_CHANGED_EVENT, cb);
  };
}

// useSyncExternalStore needs a stable snapshot per state, so cache by value.
const snapshotCache = new Map<string, { key: string; state: BotDraftState }>();

function draftSnapshot(botId: string): BotDraftState {
  const state = readBotDraftState(botId);
  const key = `${state.config}|${state.workflowIDs.join(',')}`;
  const cached = snapshotCache.get(botId);
  if (cached && cached.key === key) return cached.state;
  snapshotCache.set(botId, { key, state });
  return state;
}

/**
 * Reactive persisted-draft state for one bot — updates on writes in this tab
 * and on writes (including publishes) in other tabs.
 */
export function useBotDraftState(botId: string): BotDraftState {
  return useSyncExternalStore(
    subscribeDrafts,
    () => draftSnapshot(botId),
    () => EMPTY_DRAFT_STATE,
  );
}
